





















import { Component, Vue, Ref } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { ProjectSupervisorSource } from "@/data/models/Settings";
import User from "@/data/models/User";
import { UserModule } from "@/store/modules/UserModule";
import {
  getProjectOwnerId,
  ProjectDetails,
} from "@/data/models/ProjectDetails";
import {
  getProjectManagement,
  getProjectSupervisorSource,
} from "@/data/repository/ClientRepository";

import UserPickerComponent from "@/components/common/UserPickerComponent.vue";
import {
  getActiveUser,
  filterUsersForSelection,
} from "@/data/repository/UserRepository";
import { editProjectAccess, officeAccess, projectSupervisionAccess } from "@/permissions/UserPermissions";
import { RoleSettingsModule } from "@/store/modules/RoleSettingsModule";

export interface ProjectOwnershipUpdate {
  hasOwnershipChanged: boolean;
  newOwnerId: string | null;
  newSupervisorId: string | null;
  newOwnerStaffNumber: string | null;
  newSupervisorStaffNumber: string | null;
}

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "user-picker": UserPickerComponent,
  },
})
export default class SetProjectOwnershipComponent extends Vue {
  projectManagement = getProjectManagement();

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  resolve?: (value: ProjectOwnershipUpdate) => void = undefined;
  reject?: (reason: string) => void = undefined;

  users: User[] = [];
  projectDetails: ProjectDetails | null = null;

  projectOwnerId: string | null = null;
  projectSupervisorId: string | null = null;

  usersForProjetOwnerSelection: User[] = [];
  usersForProjetSupervisorSelection: User[] = [];

  get roleSettings() {
    return RoleSettingsModule.roleSettings;
  }

  getUsersToSelectFrom(projectDetails: ProjectDetails): User[] {
    const users = filterUsersForSelection(UserModule.users);

    const branch = projectDetails.clientData.branch;

    if (!branch) {
      return users;
    }

    return users.filter(
      (user) =>
        !user.branches ||
        user.branches.length === 0 ||
        user.branches.includes(branch)
    );
  }

  async show(projectDetails: ProjectDetails) {
    this.projectDetails = projectDetails;
    this.users = UserModule.users;

    this.usersForProjetOwnerSelection = this.getUsersToSelectFrom(
      projectDetails
    ).filter((user) => editProjectAccess(user));

    this.usersForProjetSupervisorSelection = this.getUsersToSelectFrom(
      projectDetails
    ).filter((user) => projectSupervisionAccess(user));

    this.projectOwnerId = getProjectOwnerId(projectDetails);
    this.projectSupervisorId = projectDetails.projectSupervisorId ?? null;

    if (this.projectOwnerId) {
      if (!UserModule.user(this.projectOwnerId) && projectDetails.projectOwnerStaffNumber) {
        const userIdOrStaffNumber = UserModule.userIdByStaffNumber(projectDetails.projectOwnerStaffNumber);
        if (UserModule.user(userIdOrStaffNumber)) {
          this.projectOwnerId = userIdOrStaffNumber;
        }
      }
    }

    if (this.projectSupervisorId) {
      if (!UserModule.user(this.projectSupervisorId) && projectDetails.projectSupervisorStaffNumber) {
        const userIdOrStaffNumber = UserModule.userIdByStaffNumber(projectDetails.projectSupervisorStaffNumber);
        if (UserModule.user(userIdOrStaffNumber)) {
          this.projectSupervisorId = userIdOrStaffNumber;
        }
      }
    }

    this.baseDialogComponent.show({
      title: this.$t("clientData.changeProjectOwner").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.abort").toString(),
      closeOnSave: false,
      persistent: false,
    });

    return new Promise<ProjectOwnershipUpdate>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async onSave() {
    const projectSupervisorId = this.projectSupervisorId;
    const projectOwnerId = this.projectOwnerId;

    const projectSupervisorStaffNumber = projectSupervisorId 
      ? UserModule.user(projectSupervisorId)?.staffNumber
      : null;
    const projectOwnerStaffNumber = projectOwnerId
      ? UserModule.user(projectOwnerId)?.staffNumber
      : null;

    this.baseDialogComponent.isShowing = false;

    this.resolve &&
      this.resolve({
        hasOwnershipChanged: true,
        newOwnerId: projectOwnerId ?? null,
        newSupervisorId: projectSupervisorId ?? null,
        newOwnerStaffNumber: projectOwnerStaffNumber ?? null,
        newSupervisorStaffNumber: projectSupervisorStaffNumber ?? null,
      });
  }

  getNewProjectOwnership(
    project: ProjectDetails,
    projectOwnerId: string | null | undefined,
    projectOwnerSource: string | null | undefined,
    users: User[]
  ): ProjectOwnershipUpdate {
    const previousProjectOwnerId = getProjectOwnerId(project);
    let newProjectOwnerId = projectOwnerId;

    if (!newProjectOwnerId) {
      newProjectOwnerId = previousProjectOwnerId;
    }

    const previousProjectSupervisorId = project.projectSupervisorId;

    const projectOwner = newProjectOwnerId
      ? UserModule.user(newProjectOwnerId)
      : null;

    const ownershipChangedBy = projectOwnerSource
      ? UserModule.user(projectOwnerSource)
      : null;

    let supervisorId: string | null | undefined = project.projectSupervisorId ?? null;

    const projectSupervisorSource = getProjectSupervisorSource();

    const supervisor = this.findSupervisor(newProjectOwnerId, users);
    const hasOwnerSupervisorCapability = projectSupervisionAccess(projectOwner)
    const hasChangedBySupervisorCapability = projectSupervisionAccess(ownershipChangedBy)
    const isSelfAssignment = !!ownershipChangedBy && !!newProjectOwnerId && ownershipChangedBy.id === newProjectOwnerId

    if (ownershipChangedBy) {
      if (officeAccess(ownershipChangedBy)) {
        if (!projectOwnerId) {
          supervisorId = null
          newProjectOwnerId = null
        }
      } else {
        if (hasOwnerSupervisorCapability && newProjectOwnerId) {
          supervisorId = newProjectOwnerId
        } else if (hasChangedBySupervisorCapability) {
          if (projectSupervisorSource === ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER) {
            supervisorId = supervisor?.id ?? newProjectOwnerId ?? null
          } else {
            supervisorId = ownershipChangedBy.id
          }
        } else if (isSelfAssignment) {
          if (projectSupervisorSource === ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER) {
            supervisorId = supervisor?.id ?? newProjectOwnerId ?? null
          } else {
            supervisorId = previousProjectSupervisorId ?? project.projectSupervisorId ?? null
          }
        } else if (projectSupervisorSource === ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER) {
          supervisorId = supervisor?.id ?? newProjectOwnerId ?? null
        } else {
          supervisorId = previousProjectSupervisorId ?? project.projectSupervisorId ?? null
        }
      }
    } else {
      if (hasOwnerSupervisorCapability && newProjectOwnerId) {
        supervisorId = newProjectOwnerId
      } else if (projectSupervisorSource === ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER) {
        supervisorId = supervisor?.id ?? newProjectOwnerId ?? null
      } else {
        supervisorId = previousProjectSupervisorId ?? project.projectSupervisorId ?? null
      }
    }

    const hasOwnershipChanged =
      (previousProjectOwnerId ?? null) !== (newProjectOwnerId ?? null) ||
      (project.projectSupervisorId ?? null) !== (supervisorId ?? null);

    const newProjectOwnerStaffNumber = newProjectOwnerId ? UserModule.user(newProjectOwnerId)?.staffNumber : null;
    const supervisorStaffNumber = supervisorId ? UserModule.user(supervisorId)?.staffNumber : null;

    return {
      hasOwnershipChanged: hasOwnershipChanged,
      newOwnerId: newProjectOwnerId ?? null,
      newSupervisorId: supervisorId ?? null,
      newOwnerStaffNumber: newProjectOwnerStaffNumber ?? null,
      newSupervisorStaffNumber: supervisorStaffNumber ?? null,
    };
  }

  findSupervisor(
    userId: string | undefined | null,
    users: User[],
  ) {
    if (!userId) {
      return null;
    }

    const groupedUseres = new Map<string, User>();

    users.forEach((user) => {
      groupedUseres.set(user.id, user);
    });

    const projectOwner = groupedUseres.get(userId);

    if (!projectOwner) {
      return null;
    }

    if (projectSupervisionAccess(projectOwner)) {
      return projectOwner;
    }

    const visited = new Set<string>();

    let toVisit = projectOwner.supervisorId;

    while (toVisit && !visited.has(toVisit)) {
      const user = groupedUseres.get(toVisit);

      if (!user) {
        return projectOwner;
      }

      if (projectSupervisionAccess(user)) {
        return user;
      }

      visited.add(toVisit);

      toVisit = user.supervisorId;
    }

    return projectOwner;
  }

  onProjectOwnerChanged(newProjectOwner: User | null) {
    const projectDetails = this.projectDetails;

    if (!projectDetails) {
      return;
    }

    const update = this.getNewProjectOwnership(
      projectDetails,
      newProjectOwner?.id,
      getActiveUser()?.id,
      UserModule.users
    );

    if (update.newSupervisorId) {
      const newSupervisor = UserModule.user(update.newSupervisorId);

      if (newSupervisor) {
        this.projectSupervisorId = newSupervisor.id;
      }
    }
  }
}
